import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { adminApi } from '../../utils/api';
import { Button, Badge, Card, Tabs, Spinner, InfoRow, Alert, Input, Modal } from './shared';

const VIEW_TABS = [
    { id: 'general', label: 'General' },
    { id: 'prompt', label: 'System Prompt' },
    { id: 'capabilities', label: 'Capabilities' },
    { id: 'tools_safety', label: 'Tools & Safety' },
];

const VALID_VIEW_TABS = VIEW_TABS.map(t => t.id);
const DEFAULT_TAB = 'general';

export default function AdminAgentView() {
    const { agentId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [agent, setAgent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const tabParam = searchParams.get('tab');
    const tab = VALID_VIEW_TABS.includes(tabParam) ? tabParam : DEFAULT_TAB;
    const setTab = (next) => {
        const params = new URLSearchParams(searchParams);
        if (next && next !== DEFAULT_TAB) {
            params.set('tab', next);
        } else {
            params.delete('tab');
        }
        setSearchParams(params, { replace: true });
    };
    const [seedModal, setSeedModal] = useState(false);
    const [seedConfirm, setSeedConfirm] = useState('');
    const [seedLoading, setSeedLoading] = useState(false);
    const [seedError, setSeedError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [promptView, setPromptView] = useState('raw');
    const [reminders, setReminders] = useState([]);

    useEffect(() => {
        loadAgent();
        loadReminders();
    }, [agentId]);

    const loadAgent = async () => {
        try {
            setLoading(true);
            const data = await adminApi.getAgentFull(agentId);
            setAgent(data);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load agent');
        } finally {
            setLoading(false);
        }
    };

    const handleSeedPrompt = async () => {
        if (seedConfirm !== 'confirm') return;
        setSeedLoading(true);
        setSeedError(null);
        try {
            await adminApi.seedAgentPrompt(agentId, { useDefault: true, force: !!agent.system_prompt });
            setSeedModal(false);
            setSeedConfirm('');
            setSuccessMessage('System prompt seeded successfully');
            setTimeout(() => setSuccessMessage(null), 4000);
            await loadAgent();
        } catch (err) {
            setSeedError(err.message || 'Failed to seed prompt');
        } finally {
            setSeedLoading(false);
        }
    };

    const loadReminders = async () => {
        try {
            const res = await adminApi.listReminders();
            setReminders(res.reminders || []);
        } catch {}
    };

    const agentReminders = reminders.filter(r => {
        if (!agent) return false;
        const include = agent.reminders_include;
        const exclude = agent.reminders_exclude;
        if (include && include.length > 0) return include.includes(r.id);
        if (exclude && exclude.length > 0) return !exclude.includes(r.id);
        return r.is_active;
    });

    if (loading) return <Spinner size="lg" text="Loading agent..." center />;
    if (error) return <Alert variant="error">{error}</Alert>;
    if (!agent) return null;

    return (
        <div>
            {successMessage && (
                <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">
                    {successMessage}
                </Alert>
            )}

            <Modal
                open={seedModal}
                onClose={() => { setSeedModal(false); setSeedConfirm(''); setSeedError(null); }}
                title="Seed Default System Prompt"
                description={agent.system_prompt ? 'Agent đã có system prompt. Hành động này sẽ ghi đè lên nội dung hiện tại.' : 'Seed DEFAULT_SYSTEM_PROMPT từ code vào DB cho agent này.'}
                size="sm"
            >
                <Modal.Body>
                    {seedError && <Alert variant="error">{seedError}</Alert>}
                    <p className="text-sm text-gray-600">
                        Gõ <span className="font-mono font-semibold text-gray-900">confirm</span> để tiếp tục.
                    </p>
                    <Input
                        value={seedConfirm}
                        onChange={(e) => setSeedConfirm(e.target.value)}
                        placeholder="confirm"
                        onKeyDown={(e) => e.key === 'Enter' && seedConfirm === 'confirm' && handleSeedPrompt()}
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setSeedModal(false); setSeedConfirm(''); setSeedError(null); }}>
                        Cancel
                    </Button>
                    <Button variant="danger" disabled={seedConfirm !== 'confirm' || seedLoading} onClick={handleSeedPrompt}>
                        {seedLoading ? 'Seeding...' : 'Seed Prompt'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="flex items-center gap-3 mb-6">
                <Link to="/admin/agents" className="text-gray-400 hover:text-gray-600 shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                    </svg>
                </Link>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-semibold text-gray-900 truncate">{agent.name}</h1>
                        <Badge variant="outline">{agent.id}</Badge>
                        <Badge variant={agent.agent_type === 'default' ? 'info' : agent.agent_type === 'partner' ? 'purple' : agent.agent_type === 'voice' ? 'success' : 'default'}>
                            {agent.agent_type}
                        </Badge>
                        {agent.is_builtin && <Badge variant="success">Built-in</Badge>}
                    </div>
                    {agent.description && <p className="text-sm text-gray-500 mt-0.5">{agent.description}</p>}
                </div>
                <Button
                    size="sm"
                    onClick={() => {
                        const suffix = tab && tab !== DEFAULT_TAB ? `?tab=${tab}` : '';
                        navigate(`/admin/agents/${agentId}/edit${suffix}`);
                    }}
                >
                    Edit
                </Button>
            </div>

            <Tabs tabs={VIEW_TABS} active={tab} onChange={setTab} className="mb-6" />

            {tab === 'general' && (
                <div className="space-y-4">
                    <Card title="Identity">
                        <InfoRow label="ID" value={agent.id} mono />
                        <InfoRow label="Type" value={<Badge variant="info">{agent.agent_type}</Badge>} />
                        <InfoRow label="Owner" value={<Badge variant={agent.owner_type === 'system' ? 'info' : agent.owner_type === 'partner' ? 'purple' : 'success'}>{agent.owner_type}</Badge>} />
                        {agent.owner_id && <InfoRow label="Owner ID" value={agent.owner_id} mono />}
                        <InfoRow label="Public" value={agent.is_public ? 'Yes' : 'No'} />
                        <InfoRow label="Built-in" value={agent.is_builtin ? 'Yes' : 'No'} />
                    </Card>
                    <Card title="Model & Reasoning">
                        <InfoRow label="Model" value={agent.model_name} mono />
                        <InfoRow label="Temperature" value={agent.temperature} />
                        <InfoRow label="Max Tokens" value={agent.max_tokens || 'Default'} />
                        <InfoRow label="Max Response" value={agent.max_response_tokens || 'Default'} />
                        <InfoRow label="Reasoning" value={agent.reasoning_enabled ? `Enabled (${agent.reasoning_effort || 'medium'})` : 'Disabled'} />
                    </Card>
                    <Card title="Voice">
                        <InfoRow label="Status" value={agent.voice_enabled ? 'Enabled' : 'Disabled'} />
                        {agent.voice_enabled && (
                            <>
                                <InfoRow label="Model" value={agent.voice_model || 'Default'} />
                                <InfoRow label="Speed" value={agent.voice_speed || 1.0} />
                            </>
                        )}
                    </Card>

                    <Card title="Brand Display">
                        {agent.brand ? (
                            <>
                                {agent.brand.default && (
                                    <InfoRow label="Default" value={`${agent.brand.default.name} (${agent.brand.default.icon}, ${agent.brand.default.color})`} />
                                )}
                                {agent.brand.session_types && Object.entries(agent.brand.session_types).map(([type, config]) => (
                                    <InfoRow key={type} label={`Override: ${type}`} value={`${config.name} (${config.icon}, ${config.color})`} />
                                ))}
                            </>
                        ) : (
                            <InfoRow label="Status" value="Not configured" />
                        )}
                    </Card>
                </div>
            )}

            {tab === 'prompt' && (
                <Card>
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-500">
                            {agent.system_prompt
                                ? <>
                                    <span>{agent.system_prompt.length.toLocaleString()} chars</span>
                                    <span className="mx-1.5 text-gray-300">·</span>
                                    <span>~{Math.ceil(agent.system_prompt.length / 4).toLocaleString()} tokens</span>
                                  </>
                                : 'No system prompt — falling back to DEFAULT_SYSTEM_PROMPT'}
                        </span>
                        <div className="flex items-center gap-2">
                            <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs">
                                <button
                                    className={`px-3 py-1.5 font-medium transition-colors ${promptView === 'raw' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    onClick={() => setPromptView('raw')}
                                >
                                    Raw
                                </button>
                                <button
                                    className={`px-3 py-1.5 font-medium transition-colors border-l border-gray-200 ${promptView === 'markdown' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    onClick={() => setPromptView('markdown')}
                                >
                                    Markdown
                                </button>
                            </div>
                            <Button size="sm" variant={agent.system_prompt ? 'danger' : 'primary'} onClick={() => setSeedModal(true)}>
                                {agent.system_prompt ? 'Re-seed Default' : 'Seed Default Prompt'}
                            </Button>
                        </div>
                    </div>
                    {promptView === 'raw' ? (
                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[70vh]">
                            {agent.system_prompt || 'No system prompt configured'}
                        </pre>
                    ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-y-auto max-h-[70vh] prose prose-sm max-w-none text-gray-800">
                            {agent.system_prompt
                                ? <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{agent.system_prompt}</ReactMarkdown>
                                : <span className="text-gray-400 italic">No system prompt configured</span>
                            }
                        </div>
                    )}
                </Card>
            )}

            {tab === 'capabilities' && (
                <div className="space-y-4">
                    <Card title={<span className="flex items-center gap-2">Skills <Badge variant={agent.skills_enabled ? 'success' : 'outline'}>{agent.skills_enabled ? 'Enabled' : 'Disabled'}</Badge></span>}>
                        {(agent.skills_loaded || []).length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">No skills loaded</p>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {agent.skills_loaded.map(s => (
                                    <div key={s.id} className="py-2.5">
                                        <span className="text-sm font-medium text-gray-900">{s.name}</span>
                                        {s.description && <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                        {agent.skills_include && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                                <span className="text-xs text-gray-500">Include: </span>
                                {agent.skills_include.map(id => <Badge key={id} variant="info" className="mr-1">{id}</Badge>)}
                            </div>
                        )}
                        {agent.skills_exclude && (
                            <div className="mt-1">
                                <span className="text-xs text-gray-500">Exclude: </span>
                                {agent.skills_exclude.map(id => <Badge key={id} variant="danger" className="mr-1">{id}</Badge>)}
                            </div>
                        )}
                        <div className="mt-1">
                            <span className="text-xs text-gray-500">Preload: </span>
                            {agent.skills_preload === null || agent.skills_preload === undefined ? (
                                <Badge variant="outline">All accessible</Badge>
                            ) : agent.skills_preload.length === 0 ? (
                                <Badge variant="outline">None (discover via ls)</Badge>
                            ) : (
                                agent.skills_preload.map(id => <Badge key={id} variant="purple" className="mr-1">{id}</Badge>)
                            )}
                        </div>
                    </Card>

                    <Card title={<span className="flex items-center gap-2">Subagents <Badge variant={agent.subagents?.enabled !== false ? 'success' : 'outline'}>{agent.subagents?.enabled !== false ? 'Enabled' : 'Disabled'}</Badge>{agent.subagents?.enabled !== false && <Badge variant={agent.subagents?.stream_subgraphs !== false ? 'success' : 'outline'}>{agent.subagents?.stream_subgraphs !== false ? 'Stream: on' : 'Stream: off'}</Badge>}</span>}>
                        {(agent.subagents_loaded || []).length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">No subagents loaded</p>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {agent.subagents_loaded.map(s => (
                                    <div key={s.id} className="flex items-center justify-between py-2.5">
                                        <div>
                                            <span className="text-sm font-medium text-gray-900">{s.name}</span>
                                            {s.model_name && <span className="text-xs text-gray-400 ml-2">{s.model_name}</span>}
                                        </div>
                                        <Link to={`/admin/subagents/${s.id}`}><Badge variant="outline">{s.id}</Badge></Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card title={<span className="flex items-center gap-2">Reminders <Badge variant={agent.reminders_enabled !== false ? 'success' : 'outline'}>{agent.reminders_enabled !== false ? 'Enabled' : 'Disabled'}</Badge>{agent.reminders_enabled !== false && <span className="text-xs text-gray-400 font-normal">Max {agent.reminders_max_per_turn ?? 5}/turn</span>}</span>}>
                        {agentReminders.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">No reminders configured</p>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {agentReminders.map(r => (
                                    <div key={r.id} className="py-2.5">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Link to={`/admin/reminders/${r.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600">{r.name}</Link>
                                                <Badge variant="outline">{r.id}</Badge>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <Badge variant="info">{r.trigger}</Badge>
                                                <span className="text-xs text-gray-400">P{r.priority}</span>
                                                {r.group && <Badge variant="outline">{r.group}</Badge>}
                                            </div>
                                        </div>
                                        {r.conditions?.length > 0 && (
                                            <div className="flex gap-1 mt-1 flex-wrap">
                                                {r.conditions.map((c, i) => (
                                                    <span key={i} className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                                        {c.type}{c.interval ? `:${c.interval}` : ''}{c.key ? `:${c.key}` : ''}{c.tool_name ? `:${c.tool_name}` : ''}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card title="Knowledge" headerRight={
                        <Link to="/admin/knowledge" className="text-xs text-blue-600 hover:text-blue-800">Manage</Link>
                    }>
                        {(agent.knowledge_loaded || []).length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">No knowledge loaded</p>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {agent.knowledge_loaded.map(k => (
                                    <div key={k.id} className="flex items-center justify-between py-2.5">
                                        <div>
                                            <span className="text-sm font-medium text-gray-900">{k.topic}</span>
                                            {k.description && <p className="text-xs text-gray-500 mt-0.5">{k.description}</p>}
                                        </div>
                                        <Link to={`/admin/knowledge/${k.id}`}><Badge variant="outline">{k.id}</Badge></Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <CapabilityCard title="Context Editing" dict={agent.context_editing}>
                        <ContextEditingDetails
                            dict={agent.context_editing}
                            defaults={agent.agent_defaults?.capabilities?.context_editing || {}}
                        />
                    </CapabilityCard>

                    <CapabilityCard title="Summarization" dict={agent.summarization}>
                        <SummarizationDetails dict={agent.summarization} />
                    </CapabilityCard>

                    <CapabilityCard title="Long-term Memory" dict={agent.long_term_memory}>
                        <p className="text-xs text-gray-500">
                            Injects the long-term memory system prompt so the agent can read/write <code className="px-1 py-0.5 bg-gray-100 rounded">/memories/*</code>.
                        </p>
                    </CapabilityCard>

                    <CapabilityCard title="Image Search" dict={agent.image_search}>
                        <p className="text-xs text-gray-500">Enables image search guidance for the web search tool.</p>
                    </CapabilityCard>

                    <CapabilityCard title="Visualizer" dict={agent.visualizer}>
                        <p className="text-xs text-gray-500">
                            Enables visualizer system prompt for <code className="px-1 py-0.5 bg-gray-100 rounded">visualize_show_chart</code> workflows.
                        </p>
                    </CapabilityCard>

                    <CapabilityCard title="Sandbox" dict={agent.sandbox}>
                        <InfoRow
                            label="Simplize SDK"
                            value={
                                (agent.sandbox?.simplize_sdk ?? true) !== false
                                    ? <Badge variant="success">Enabled</Badge>
                                    : <Badge variant="outline">Disabled</Badge>
                            }
                        />
                    </CapabilityCard>
                </div>
            )}

            {tab === 'tools_safety' && (
                <div className="space-y-4">
                    <Card title={<span className="flex items-center gap-2">MCP Servers <Badge variant={agent.mcp_enabled ? 'success' : 'outline'}>{agent.mcp_enabled ? 'Enabled' : 'Disabled'}</Badge></span>}>
                        {agent.mcp_include && (
                            <div className="mb-2">
                                <span className="text-xs text-gray-500">Include: </span>
                                {agent.mcp_include.map(id => <Badge key={id} variant="info" className="mr-1">{id}</Badge>)}
                            </div>
                        )}
                        {agent.mcp_exclude && (
                            <div className="mb-2">
                                <span className="text-xs text-gray-500">Exclude: </span>
                                {agent.mcp_exclude.map(id => <Badge key={id} variant="danger" className="mr-1">{id}</Badge>)}
                            </div>
                        )}
                        {(agent.mcp_servers_available || []).length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">No MCP servers available</p>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {agent.mcp_servers_available.map(s => (
                                    <div key={s.id} className="flex items-center justify-between py-2">
                                        <div>
                                            <span className="text-sm font-medium text-gray-900">{s.name}</span>
                                            {s.description && <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>}
                                        </div>
                                        <div className="flex gap-1">
                                            <Badge variant="outline">{s.transport_type}</Badge>
                                            <Badge variant={s.is_active ? 'success' : 'danger'} dot>{s.is_active ? 'Active' : 'Inactive'}</Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card title={<span className="flex items-center gap-2">Tools <Badge variant={agent.tools?.enabled !== false ? 'success' : 'outline'}>{agent.tools?.enabled !== false ? 'Enabled' : 'Disabled'}</Badge><Badge variant="outline">{(agent.tools_loaded || []).length} tools</Badge></span>}>
                        {agent.tools?.include_tags?.length > 0 && (
                            <div className="mb-2">
                                <span className="text-xs text-gray-500">Include Tags: </span>
                                {agent.tools.include_tags.map(tag => <Badge key={tag} variant="info" className="mr-1">{tag}</Badge>)}
                            </div>
                        )}
                        {agent.tools?.exclude_tags?.length > 0 && (
                            <div className="mb-2">
                                <span className="text-xs text-gray-500">Exclude Tags: </span>
                                {agent.tools.exclude_tags.map(tag => <Badge key={tag} variant="danger" className="mr-1">{tag}</Badge>)}
                            </div>
                        )}
                        {agent.tools?.include?.length > 0 && (
                            <div className="mb-2">
                                <span className="text-xs text-gray-500">Include: </span>
                                {agent.tools.include.map(id => <Badge key={id} variant="info" className="mr-1">{id}</Badge>)}
                            </div>
                        )}
                        {agent.tools?.exclude?.length > 0 && (
                            <div className="mb-2">
                                <span className="text-xs text-gray-500">Exclude: </span>
                                {agent.tools.exclude.map(id => <Badge key={id} variant="danger" className="mr-1">{id}</Badge>)}
                            </div>
                        )}
                        {(agent.tools_loaded || []).length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">No tools loaded</p>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {agent.tools_loaded.map(t => (
                                    <div key={t.name} className="flex items-center justify-between py-2">
                                        <span className="text-xs font-mono text-gray-900">{t.name}</span>
                                        <div className="flex gap-1">
                                            {t.toolkit && <Badge variant="default">{t.toolkit}</Badge>}
                                            <Badge variant="outline">{t.source}</Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card title={<span className="flex items-center gap-2">HITL / Interrupt <Badge variant={agent.hitl_enabled ? 'success' : 'danger'}>{agent.hitl_enabled ? 'Enabled' : 'Disabled'}</Badge>{agent.hitl_enabled && <span className="text-xs text-gray-400 font-normal">{agent.interrupt_on ? 'Custom' : 'Default'} ({Object.keys(agent.resolved_interrupt_on || agent.interrupt_on || {}).length} tools)</span>}</span>}>
                        {agent.hitl_enabled && (
                            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                                {Object.entries(agent.resolved_interrupt_on || agent.interrupt_on || {}).map(([tool, config]) => (
                                    <div key={tool} className="flex items-center justify-between py-2">
                                        <span className="text-xs font-mono text-gray-900">{tool}</span>
                                        <div className="flex gap-1">
                                            {(config.allowed_decisions || []).map(d => (
                                                <Badge key={d} variant={d === 'approve' ? 'success' : d === 'reject' ? 'danger' : 'info'}>{d}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>
            )}
        </div>
    );
}

const capabilityOn = (dict) => dict == null || dict.enabled !== false;

function CapabilityCard({ title, dict, children }) {
    const enabled = capabilityOn(dict);
    return (
        <Card title={
            <span className="flex items-center gap-2">
                {title}
                <Badge variant={enabled ? 'success' : 'outline'}>{enabled ? 'Enabled' : 'Disabled'}</Badge>
            </span>
        }>
            {enabled ? children : (
                <p className="text-xs text-gray-400 italic py-1">Disabled — related system prompt and middleware are skipped.</p>
            )}
        </Card>
    );
}

const formatDefaultDisplay = (val) => {
    if (val === null || val === undefined) return '—';
    if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
    if (typeof val === 'object') {
        const entries = Object.entries(val);
        return entries.length
            ? entries.map(([k, v]) => `${k}→${Array.isArray(v) ? v.join(',') : v}`).join(' · ')
            : '—';
    }
    return String(val);
};

const isEmptyValue = (val) => {
    if (val === null || val === undefined || val === '') return true;
    if (Array.isArray(val)) return val.length === 0;
    if (typeof val === 'object') return Object.keys(val).length === 0;
    return false;
};

function ContextEditingListRow({ label, items, defaultValue }) {
    if (isEmptyValue(items)) {
        return (
            <InfoRow
                label={label}
                value={<span className="text-gray-400 italic">default: {formatDefaultDisplay(defaultValue)}</span>}
            />
        );
    }
    if (Array.isArray(items)) {
        return (
            <InfoRow label={label} value={
                <div className="flex flex-wrap gap-1">
                    {items.map(item => <Badge key={item} variant="outline">{item}</Badge>)}
                </div>
            } />
        );
    }
    return (
        <InfoRow label={label} value={
            <div className="flex flex-wrap gap-1">
                {Object.entries(items).map(([k, v]) => (
                    <Badge key={k} variant="outline">
                        {k} → {Array.isArray(v) ? v.join(', ') : String(v)}
                    </Badge>
                ))}
            </div>
        } />
    );
}

function ContextEditingDetails({ dict, defaults = {} }) {
    const value = dict || {};
    const pct = value.protect_current_turn || {};
    const pctDefaults = defaults.protect_current_turn || {};
    const pctEnabled = pct.enabled ?? pctDefaults.enabled ?? true;
    const pctMode = pct.mode || pctDefaults.mode || 'all';
    const renderScalar = (key) => {
        const raw = value[key];
        if (isEmptyValue(raw)) {
            return <span className="text-gray-400 italic">default: {formatDefaultDisplay(defaults[key])}</span>;
        }
        return String(raw);
    };
    return (
        <div className="space-y-0.5">
            <InfoRow label="Trigger" value={renderScalar('trigger')} />
            <InfoRow label="Keep" value={renderScalar('keep')} />
            <InfoRow label="Clear at least" value={renderScalar('clear_at_least')} />
            <InfoRow label="Approximate multiplier" value={renderScalar('approximate_multiplier')} />
            <ContextEditingListRow label="Exclude tools" items={value.exclude_tools} defaultValue={defaults.exclude_tools} />
            <ContextEditingListRow label="Offload tools" items={value.offload_tools} defaultValue={defaults.offload_tools} />
            <ContextEditingListRow label="Exclude tool patterns" items={value.exclude_tool_patterns} defaultValue={defaults.exclude_tool_patterns} />
            <ContextEditingListRow label="Offload input tools" items={value.offload_input_tools} defaultValue={defaults.offload_input_tools} />
            <InfoRow
                label="Protect current turn"
                value={
                    pctEnabled
                        ? <Badge variant="success">{pctMode === 'all' ? 'All (absolute)' : `Custom (trigger: ${pct.trigger ?? pctDefaults.trigger ?? '—'}, keep: ${pct.keep ?? pctDefaults.keep ?? '—'})`}</Badge>
                        : <Badge variant="outline">Disabled</Badge>
                }
            />
        </div>
    );
}

function SummarizationDetails({ dict }) {
    const value = dict || {};
    const formatContextSize = (v) => {
        if (!v) return <span className="text-gray-400 italic">model default</span>;
        if (Array.isArray(v) && v.length === 2) return `${v[0]} ${v[1]}`;
        return String(v);
    };
    const truncArgs = value.truncate_args_settings || {};
    return (
        <div className="space-y-1 text-sm">
            <InfoRow label="Trigger" value={formatContextSize(value.trigger)} />
            <InfoRow label="Keep" value={formatContextSize(value.keep)} />
            <InfoRow label="Trim tokens to summarize" value={value.trim_tokens_to_summarize ?? <span className="text-gray-400 italic">default</span>} />
            <InfoRow label="History path prefix" value={value.history_path_prefix || '/conversation_history'} />
            {Object.keys(truncArgs).length > 0 && (
                <>
                    <InfoRow label="Truncate args trigger" value={formatContextSize(truncArgs.trigger)} />
                    <InfoRow label="Truncate args keep" value={formatContextSize(truncArgs.keep)} />
                </>
            )}
        </div>
    );
}
